import React, { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Descriptions, Modal, Select, Space, Table, Tag, Typography, message } from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import * as api from '@apis/grouping-agent';
import { OptionMappingReview } from '@models/grouping-agent';

const { Text, Paragraph } = Typography;

interface Props {
  runId: string;
  reviews: OptionMappingReview[];
  loading?: boolean;
}

const statusColor: Record<string, string> = {
  pending: 'orange',
  resolved: 'green',
  skipped: 'default',
};

const confidenceColor: Record<string, string> = {
  high: 'green',
  medium: 'blue',
  low: 'red',
};

const KakaoOptionMappingTab: React.FC<Props> = ({ runId, reviews, loading }) => {
  const queryClient = useQueryClient();
  const [activeReview, setActiveReview] = useState<OptionMappingReview | null>(null);
  const [selectedOptionId, setSelectedOptionId] = useState<string | undefined>();

  const pendingCount = useMemo(() => reviews.filter((r) => r.status === 'pending').length, [reviews]);

  const resolveMutation = useMutation({
    mutationFn: (params: { reviewId: string; action: 'approve' | 'skip'; resolvedOptionId?: string }) =>
      api.resolveGroupingAgentOptionMapping(runId, params.reviewId, {
        action: params.action,
        resolvedOptionId: params.resolvedOptionId,
      }),
    onSuccess: (res) => {
      const reprocess = res?.data?.groupingReprocess;
      if (reprocess?.status === 'success') {
        message.success(reprocess.message || '매핑 승인 및 그룹핑 재실행이 완료되었습니다.');
      } else if (reprocess) {
        message.warning(reprocess.message || `그룹핑 재실행: ${reprocess.status}`);
      } else {
        message.success('처리되었습니다.');
      }
      queryClient.invalidateQueries({ queryKey: ['grouping-agent-option-mapping', runId] });
      queryClient.invalidateQueries({ queryKey: ['grouping-agent-run', runId] });
      queryClient.invalidateQueries({ queryKey: ['grouping-agent-run-items', runId] });
      queryClient.invalidateQueries({ queryKey: ['grouping-agent-run-groups', runId] });
      queryClient.invalidateQueries({ queryKey: ['grouping-agent-run-groups-short', runId] });
      setActiveReview(null);
      setSelectedOptionId(undefined);
    },
    onError: (err: { response?: { data?: { message?: string | string[] } } }) => {
      const msg = err?.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join(', ') : msg || '처리에 실패했습니다.');
    },
  });

  const openReview = (review: OptionMappingReview) => {
    setActiveReview(review);
    setSelectedOptionId(review.llmSuggestion || review.candidates?.[0]?.optionId);
  };

  const columns: ColumnsType<OptionMappingReview> = [
    {
      title: '상품',
      dataIndex: 'productName',
      ellipsis: true,
      width: 220,
    },
    {
      title: 'Run 처리 회차',
      dataIndex: 'targetOptionName',
      ellipsis: true,
      width: 160,
      render: (v) => v || '-',
    },
    {
      title: '카카오 priceName',
      dataIndex: 'priceName',
      ellipsis: true,
      width: 200,
    },
    {
      title: '구매자',
      render: (_, r) => r.enrollment?.buyerName || '-',
      width: 90,
    },
    {
      title: '참가자',
      render: (_, r) => r.enrollment?.participantName || '-',
      width: 90,
    },
    {
      title: 'confidence',
      dataIndex: 'mappingConfidence',
      width: 90,
      render: (c?: string) => (c ? <Tag color={confidenceColor[c] || 'default'}>{c}</Tag> : '-'),
    },
    {
      title: 'LLM 제안 회차',
      render: (_, r) => {
        const suggested = r.candidates?.find((c) => c.optionId === r.llmSuggestion);
        return suggested?.stepLabel || r.llmSuggestion?.slice(0, 8) || '-';
      },
      ellipsis: true,
      width: 160,
    },
    {
      title: '상태',
      dataIndex: 'status',
      width: 90,
      render: (s: string) => <Tag color={statusColor[s] || 'default'}>{s}</Tag>,
    },
    {
      title: '',
      key: 'action',
      width: 100,
      render: (_, r) => (
        <Button type="link" size="small" onClick={() => openReview(r)} disabled={r.status !== 'pending'}>
          {r.status === 'pending' ? '검토' : '보기'}
        </Button>
      ),
    },
  ];

  const candidateOptions =
    activeReview?.candidates?.map((c) => ({
      value: c.optionId,
      label: `${c.stepLabel}${c.isLlmSuggestion ? ' (LLM 제안)' : ''} · similarity ${(c.similarity * 100).toFixed(0)}%`,
    })) || [];

  return (
    <>
      <Space style={{ marginBottom: 12 }}>
        <Text type="secondary">
          카카오 예약의 priceName을 앱 회차(option)에 연결합니다. pending {pendingCount}건은 그룹핑에서 제외된
          상태입니다.
        </Text>
      </Space>

      <Table
        rowKey="_id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={reviews}
        pagination={{ pageSize: 20 }}
        scroll={{ x: 1200 }}
      />

      <Modal
        title="카카오 옵션 매핑 검토"
        open={!!activeReview}
        onCancel={() => {
          setActiveReview(null);
          setSelectedOptionId(undefined);
        }}
        width={720}
        footer={
          activeReview?.status === 'pending' ? (
            <Space>
              <Button
                onClick={() => resolveMutation.mutate({ reviewId: activeReview!._id, action: 'skip' })}
                loading={resolveMutation.isPending}>
                건너뛰기
              </Button>
              <Button
                type="primary"
                disabled={!selectedOptionId}
                loading={resolveMutation.isPending}
                onClick={() =>
                  resolveMutation.mutate({
                    reviewId: activeReview!._id,
                    action: 'approve',
                    resolvedOptionId: selectedOptionId,
                  })
                }>
                승인 (매핑 적용)
              </Button>
            </Space>
          ) : (
            <Button onClick={() => setActiveReview(null)}>닫기</Button>
          )
        }>
        {activeReview && (
          <>
            <Descriptions bordered size="small" column={1} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="상품">{activeReview.productName}</Descriptions.Item>
              <Descriptions.Item label="Run 처리 회차 (상품·회차 단위)">
                {activeReview.targetOptionName || '-'}
                {activeReview.scheduleDate && (
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    ({dayjs(activeReview.scheduleDate).format('YYYY-MM-DD')})
                  </Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="카카오 priceName">{activeReview.priceName}</Descriptions.Item>
              <Descriptions.Item label="카카오 예약 ID">{activeReview.kakaoScheduleId}</Descriptions.Item>
              {activeReview.enrollment?.orderUid && (
                <Descriptions.Item label="카카오 주문 ID">{activeReview.enrollment.orderUid}</Descriptions.Item>
              )}
              <Descriptions.Item label="LLM 사유">
                <Paragraph style={{ margin: 0 }}>{activeReview.llmReason || '-'}</Paragraph>
              </Descriptions.Item>
            </Descriptions>

            <Descriptions title="구매건" bordered size="small" column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="구매자">{activeReview.enrollment?.buyerName || '-'}</Descriptions.Item>
              <Descriptions.Item label="연락처">{activeReview.enrollment?.buyerPhone || '-'}</Descriptions.Item>
              <Descriptions.Item label="참가자">{activeReview.enrollment?.participantName || '-'}</Descriptions.Item>
              <Descriptions.Item label="생년">{activeReview.enrollment?.birthYear || '-'}</Descriptions.Item>
              <Descriptions.Item label="학년">{activeReview.enrollment?.schoolYear || '-'}</Descriptions.Item>
              <Descriptions.Item label="성별">{activeReview.enrollment?.gender || '-'}</Descriptions.Item>
              <Descriptions.Item label="수업 시작" span={2}>
                {activeReview.enrollment?.startDate
                  ? dayjs(activeReview.enrollment.startDate).format('YYYY-MM-DD HH:mm')
                  : '-'}
              </Descriptions.Item>
              {activeReview.enrollment?.friends && (
                <Descriptions.Item label="친구" span={2}>
                  {activeReview.enrollment.friends}
                </Descriptions.Item>
              )}
              {activeReview.enrollment?.notes && (
                <Descriptions.Item label="비고" span={2}>
                  {activeReview.enrollment.notes}
                </Descriptions.Item>
              )}
            </Descriptions>

            {activeReview.groupingReprocess && (
              <Descriptions bordered size="small" column={1} style={{ marginBottom: 16 }}>
                <Descriptions.Item label="그룹핑 재실행">
                  <Tag color={activeReview.groupingReprocess.status === 'success' ? 'green' : 'orange'}>
                    {activeReview.groupingReprocess.mode} / {activeReview.groupingReprocess.status}
                  </Tag>
                  {activeReview.groupingReprocess.message && (
                    <Text style={{ marginLeft: 8 }}>{activeReview.groupingReprocess.message}</Text>
                  )}
                </Descriptions.Item>
              </Descriptions>
            )}

            {activeReview.status === 'pending' ? (
              <>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                  매핑할 앱 회차 선택
                </Text>
                <Select
                  style={{ width: '100%' }}
                  options={candidateOptions}
                  value={selectedOptionId}
                  onChange={setSelectedOptionId}
                  placeholder="후보 회차를 선택하세요"
                />
                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                  승인 시 카카오 매핑 적용 후 해당 회차 그룹핑이 자동 재실행됩니다.
                </Text>
              </>
            ) : (
              <Descriptions bordered size="small" column={1}>
                <Descriptions.Item label="처리 상태">
                  <Tag color={statusColor[activeReview.status]}>{activeReview.status}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="확정 회차">
                  {activeReview.candidates?.find((c) => c.optionId === activeReview.resolvedOptionId)?.stepLabel ||
                    activeReview.resolvedOptionId ||
                    '-'}
                </Descriptions.Item>
              </Descriptions>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default KakaoOptionMappingTab;
